import { useEffect, useMemo, useRef } from "react";
import { APIProvider, Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";
import type { DiscoverItem, TripStop } from "../../types";
import { API_KEY, MAP_ID, MOROCCO_CENTER } from "../map/brand-map";

interface TripMapBackdropProps {
  readonly stops: readonly TripStop[];
  readonly resolveItem: (itemId: string) => DiscoverItem | null;
  /** City coords to centre on when no stop is geocoded yet (new trips). */
  readonly fallback?: { readonly lat: number; readonly lon: number } | null;
  /**
   * Pixels at the bottom of the map hidden under the sheet. The camera frames
   * the stops in what is left above it, so a pin is never fitted underneath.
   */
  readonly bottomInset?: number;
  /**
   * "backdrop" (default): the dark brand map behind the phone's trip sheet.
   * "pane": the computer's map beside the days, light Google styling with
   * zoom buttons, filling its box instead of the screen.
   */
  readonly variant?: "backdrop" | "pane";
}

interface Plotted {
  readonly stopId: string;
  readonly title: string;
  readonly lat: number;
  readonly lon: number;
}

// Numbered navy pin (gold ring) marking a stop's position in the day order.
function numberedPinIcon(n: number): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
  <circle cx="17" cy="17" r="13" fill="#040033" stroke="#E5B265" stroke-width="2"/>
  <text x="17" y="17" text-anchor="middle" dominant-baseline="central" fill="#ffffff" font-family="Poppins, system-ui, sans-serif" font-size="14" font-weight="800">${n}</text>
</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/** The closest the camera goes when it frames the day's stops. */
const MAX_FIT_ZOOM = 15;

/**
 * How to frame the day: "centre" on one stop, or on stops that share an
 * address (a box of zero size would send fitBounds to the deepest zoom, all
 * empty tiles); "fit" the box around stops that are apart; nothing to do
 * without stops.
 */
export function cameraPlan(pins: ReadonlyArray<{ lat: number; lon: number }>): "centre" | "fit" | null {
  if (pins.length === 0) return null;
  const lats = pins.map((p) => p.lat);
  const lons = pins.map((p) => p.lon);
  const tight = Math.max(...lats) - Math.min(...lats) < 0.002 && Math.max(...lons) - Math.min(...lons) < 0.002;
  return pins.length === 1 || tight ? "centre" : "fit";
}

// Imperatively plots numbered markers for the day's stops and fits the camera
// to them (single stop → centre + mid zoom; many → fitBounds with padding).
function TripStopMarkers({ pins, bottomInset = 0 }: { pins: readonly Plotted[]; bottomInset?: number }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return undefined;
    const created: google.maps.Marker[] = [];
    const bounds = new google.maps.LatLngBounds();
    pins.forEach((p, i) => {
      const marker = new google.maps.Marker({
        position: { lat: p.lat, lng: p.lon },
        title: p.title,
        icon: {
          url: numberedPinIcon(i + 1),
          scaledSize: new google.maps.Size(34, 34),
          anchor: new google.maps.Point(17, 17),
        },
        zIndex: 100 + i,
      });
      marker.setMap(map);
      created.push(marker);
      bounds.extend({ lat: p.lat, lng: p.lon });
    });
    // Stops at one address (two activities run from the same base) give
    // fitBounds a box of zero size, and it zooms in until only empty tiles
    // show. Treat a tight cluster like one stop, and never zoom past street level.
    let idle: google.maps.MapsEventListener | null = null;
    const plan = cameraPlan(pins);
    if (plan === "centre") {
      map.setCenter(bounds.getCenter());
      map.setZoom(MAX_FIT_ZOOM - 1);
      // Lift the pin into the uncovered part of the map.
      if (bottomInset > 0) map.panBy(0, Math.round(bottomInset / 2));
    } else if (plan === "fit") {
      // The site header sits over the top of the full-bleed map too.
      map.fitBounds(bounds, {
        top: bottomInset > 0 ? 132 : 64,
        left: 48,
        right: 48,
        bottom: 64 + bottomInset,
      });
      idle = google.maps.event.addListenerOnce(map, "idle", () => {
        if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
      });
    }
    return () => {
      idle?.remove();
      for (const m of created) m.setMap(null);
    };
  }, [map, pins, bottomInset]);
  return null;
}

/**
 * Full-bleed branded Google Map behind the mobile Trips draggable sheet — the
 * same dark brand basemap the desktop Discover map uses, with numbered pins for
 * the active day's stops. Renders into a same-document div (not a cross-origin
 * iframe), so the sheet stays draggable while the exposed map pans/zooms.
 */
export function TripMapBackdrop({
  stops,
  resolveItem,
  fallback,
  bottomInset = 0,
  variant = "backdrop",
}: TripMapBackdropProps) {
  const pane = variant === "pane";
  const plotted = useMemo<Plotted[]>(() => {
    return stops
      .map((stop) => {
        const item = resolveItem(stop.itemId);
        if (!item || item.latitude === null || item.longitude === null) {
          return null;
        }
        return {
          stopId: stop.id,
          title: item.title,
          lat: item.latitude,
          lon: item.longitude,
        };
      })
      .filter((entry): entry is Plotted => entry !== null);
  }, [stops, resolveItem]);

  // First-mount camera; TripStopMarkers refines it via fitBounds once mounted.
  const center = useMemo(() => {
    if (plotted.length > 0) {
      const lat = plotted.reduce((s, p) => s + p.lat, 0) / plotted.length;
      const lng = plotted.reduce((s, p) => s + p.lon, 0) / plotted.length;
      return { lat, lng };
    }
    if (fallback) return { lat: fallback.lat, lng: fallback.lon };
    return MOROCCO_CENTER;
  }, [plotted, fallback]);
  const initialCenterRef = useRef(center);
  const initialZoomRef = useRef(plotted.length > 0 ? 12 : fallback ? 11 : 6);

  const shell = pane ? "mj-trip-day-map is-fill" : "mj-trip-map-backdrop";
  if (!API_KEY || (plotted.length === 0 && !fallback)) {
    return <div className={pane ? shell : `${shell} mj-trip-map-backdrop--empty`} aria-hidden="true" />;
  }

  return (
    <div className={shell} aria-hidden="false">
      <APIProvider apiKey={API_KEY} region="MA" language="en">
        <GoogleMap
          mapId={MAP_ID}
          defaultCenter={initialCenterRef.current}
          defaultZoom={initialZoomRef.current}
          gestureHandling={pane ? "cooperative" : "greedy"}
          disableDefaultUI={!pane}
          zoomControl={pane}
          streetViewControl={false}
          mapTypeControl={false}
          fullscreenControl={false}
          clickableIcons={false}
          style={{ width: "100%", height: "100%" }}
        >
          <TripStopMarkers pins={plotted} bottomInset={bottomInset} />
        </GoogleMap>
      </APIProvider>
      {pane ? null : <div className="mj-trip-map-backdrop__fade" aria-hidden="true" />}
    </div>
  );
}
